import { useState } from "react";
import { Shield, Globe, Bell } from "lucide-react";
import { cn } from "@/lib/utils";

interface ToggleProps {
  enabled: boolean;
  onToggle: () => void;
  label: string;
}

function Toggle({ enabled, onToggle, label }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      onClick={onToggle}
      className={cn(
        "relative h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-card",
        enabled ? "bg-accent" : "bg-border"
      )}
    >
      <span
        className={cn(
          "absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform",
          enabled && "translate-x-4"
        )}
      />
    </button>
  );
}

export function SettingsPage() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [contactForm, setContactForm] = useState(true);
  const [newFormNotification, setNewFormNotification] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  function handleToggle(
    setter: React.Dispatch<React.SetStateAction<boolean>>,
    label: string,
    currentValue: boolean
  ) {
    setter(!currentValue);
    const status = !currentValue ? "aktif" : "deaktif";
    setToast(`${label} ${status} edildi`);
    setTimeout(() => setToast(null), 2500);
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Sistem ayarları ve yapılandırma
      </p>

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in fade-in slide-in-from-bottom-4 rounded-lg border border-border bg-card px-4 py-3 text-sm font-medium text-foreground shadow-lg">
          {toast}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {/* General settings */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
              <Globe className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Genel Ayarlar
              </h3>
              <p className="text-xs text-muted-foreground">
                Site ve uygulama yapılandırması
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Site Bakım Modu
                </p>
                <p className="text-xs text-muted-foreground">
                  Aktif edildiğinde site bakım sayfası gösterilir
                </p>
              </div>
              <Toggle
                enabled={maintenanceMode}
                onToggle={() =>
                  handleToggle(
                    setMaintenanceMode,
                    "Site Bakım Modu",
                    maintenanceMode
                  )
                }
                label="Site Bakım Modu"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  İletişim Formu
                </p>
                <p className="text-xs text-muted-foreground">
                  Yeni iletişim formu gönderimlerini kabul et
                </p>
              </div>
              <Toggle
                enabled={contactForm}
                onToggle={() =>
                  handleToggle(setContactForm, "İletişim Formu", contactForm)
                }
                label="İletişim Formu"
              />
            </div>
          </div>
        </div>

        {/* Security */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
              <Shield className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Güvenlik
              </h3>
              <p className="text-xs text-muted-foreground">
                Erişim ve kimlik doğrulama
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="rounded-lg bg-secondary/50 px-4 py-3">
              <p className="text-sm font-medium text-foreground">
                Cloudflare Access
              </p>
              <p className="text-xs text-muted-foreground">
                Kimlik doğrulama Cloudflare Access ile yönetilmektedir
              </p>
            </div>
            <div className="rounded-lg bg-secondary/50 px-4 py-3">
              <p className="text-sm font-medium text-foreground">
                Admin Oturumları
              </p>
              <p className="text-xs text-muted-foreground">
                Oturum süresi: 24 saat
              </p>
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary">
              <Bell className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">
                Bildirimler
              </h3>
              <p className="text-xs text-muted-foreground">
                E-posta ve anlık bildirim ayarları
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between rounded-lg bg-secondary/50 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-foreground">
                  Yeni Form Bildirimi
                </p>
                <p className="text-xs text-muted-foreground">
                  Yeni iletişim formu geldiğinde e-posta gönder
                </p>
              </div>
              <Toggle
                enabled={newFormNotification}
                onToggle={() =>
                  handleToggle(
                    setNewFormNotification,
                    "Yeni Form Bildirimi",
                    newFormNotification
                  )
                }
                label="Yeni Form Bildirimi"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
